import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { Typography, Box, Stack, useTheme } from "@mui/material";
import { createInvestigation } from "../../../../../apis";
import { InvestigationButton } from "../components/InvestigationButton";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { ConfirmationContainer } from "../../../../Modal/Confirmation/Confirmation";

export const AddInvestigation = ({
  show,
  onHide,
  id,
  reportRender = () => {},
  ethiquin = "inspector",
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const [investigationName, setInvestigationName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!show) {
      setInvestigationName("");
    }
  }, [show]);

  const hdlInvNameChange = (e) => {
    const value = e.target.value;
    const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
    setInvestigationName(capitalized);
  };

  // Create New|Additional investigation Advanced|Simple
  const handleCreateInvestigation = async (type) => {
    const dataToSave = {
      id,
      type,
      title: investigationName,
    };
    setIsLoading(true);

    const sending = enqueueSnackbar(`${t("Care.processing_request")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await createInvestigation(dataToSave);
      setInvestigationName("");
      enqueueSnackbar(t("Care.investigation_successfully_created"), {
        variant: "success",
      });
      onHide();
    } catch (error) {
      console.error(error);
    } finally {
      closeSnackbar(sending);
      setIsLoading(false);
      reportRender();
    }
  };

  return (
    <ConfirmationContainer
      show={show}
      onHide={onHide}
      ethiquin={ethiquin}
      sx={{ p: 3 }}
    >
      <Typography
        fontSize={28}
        fontWeight={700}
        color="secondary"
        textAlign="center"
        mb={2}
      >
        {t("modals.addInvestigation.title")}
      </Typography>
      <EGInput
        label={t("modals.addInvestigation.name")}
        value={investigationName}
        onChange={hdlInvNameChange}
      />
      <Typography
        color="primary.main"
        mt={1.2}
        mb={0.8}
        textAlign="center"
        fontSize={16}
        fontWeight={600}
      >
        {t("modals.addInvestigation.type")}
      </Typography>
      <Box textAlign="justify" mb={1}>
        <Typography
          component="span"
          color="primary.main"
          fontWeight={600}
          fontSize={16}
        >
          {`${t("modals.addInvestigation.type_advanced")}:`}&nbsp;
        </Typography>
        <Typography component="span" color="oxfordBlue.main" fontSize={14}>
          {t("modals.addInvestigation.type_advanced_description")}
        </Typography>
      </Box>
      <Box textAlign="justify" mb={4}>
        <Typography
          component="span"
          color="success.main"
          fontWeight={600}
          fontSize={16}
        >
          {`${t("modals.addInvestigation.type_basic")}:`}&nbsp;
        </Typography>
        <Typography component="span" color="oxfordBlue.main" fontSize={14}>
          {t("modals.addInvestigation.type_basic_description")}
        </Typography>
      </Box>
      <Stack direction="row" spacing={2} mt={2} justifyContent="center">
        <InvestigationButton
          label={t("modals.addInvestigation.type_advanced").toUpperCase()}
          onClick={() => handleCreateInvestigation("advance")}
          icon={"search_setting_bold"}
          iconHover={"outline_search_setting"}
          variant="main"
          iconColorHover={theme.palette.primary.main}
          size="medium"
          inverted
          loading={isLoading}
        />
        <InvestigationButton
          label={t("modals.addInvestigation.type_basic").toUpperCase()}
          onClick={() => handleCreateInvestigation("simple")}
          icon={"outline_search"}
          iconHover={"search_bold"}
          variant="success"
          iconColorHover={theme.palette.green.main}
          size="medium"
          inverted
          loading={isLoading}
        />
      </Stack>
    </ConfirmationContainer>
  );
};
